import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useConnection, useSession, toast } from "@/lib/store";
import { connectRealtime } from "@/lib/ws";
import { upsertMessage } from "./useChat";

/**
 * Conecta o WebSocket autenticado e aplica os eventos na UI. Ao reconectar,
 * dispara resync das listas (RF-UX-008/053). Dedupe por `message_id`/
 * `client_message_id` acontece em `upsertMessage` (EC-UX-021).
 */
export function useRealtime(): void {
  const qc = useQueryClient();
  const accessToken = useSession((s) => s.accessToken);
  const tenantId = useSession((s) => s.tenantId);
  const setConn = useConnection((s) => s.setConn);

  useEffect(() => {
    if (!accessToken || !tenantId) return;
    const handle = connectRealtime({
      onStatus: (status) =>
        setConn(status === "open" ? "online" : status === "closed" ? "offline" : "reconnecting"),
      onResync: () => {
        void qc.invalidateQueries({ queryKey: ["conversations"] });
        void qc.invalidateQueries({ queryKey: ["notifications"] });
      },
      onEvent: (event) => {
        switch (event.type) {
          case "message.created":
          case "message.updated":
            upsertMessage(qc, event.message);
            void qc.invalidateQueries({ queryKey: ["conversations"] });
            break;
          case "conversation.updated":
            qc.setQueryData(["conversation", event.conversation.id], event.conversation);
            void qc.invalidateQueries({ queryKey: ["conversations"] });
            break;
          case "notification.created":
            void qc.invalidateQueries({ queryKey: ["notifications"] });
            toast({
              tone: "info",
              message: event.notification.title,
              actionLabel: event.notification.conversationId ? "Abrir" : undefined,
              onAction: event.notification.conversationId
                ? () => window.location.assign(`/c/${event.notification.conversationId}`)
                : undefined,
            });
            break;
          case "channel.updated":
            void qc.invalidateQueries({ queryKey: ["channels"] });
            break;
        }
      },
    });
    return () => handle.close();
  }, [accessToken, tenantId, qc, setConn]);
}
